import {useSelector} from 'react-redux';

import {
    CONVERSION_TRACKING_ID_EMAIL_LEAD,
    CONVERSION_TRACKING_ID_SALE,
    IRE_CLICK_ID,
} from 'constants/analytics';
import {CartItemType} from 'desktop/pages/cart/types';
import {getOneTrustActiveGroups} from 'features/oneTrust/oneTrust';
import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';
import {logWithSentry} from 'libs/sentry/sentry';
import {localStore} from 'libs/storage/storage';
import {getUrlParams} from 'libs/url/url';

import {preparationGenerateClickId} from '../clickId/clickId';
import {getCategoryListFromLocalStorage} from '../getCategoryListFromLocalStorage/getCategoryListFromLocalStorage';
import {getParsedOrderData} from '../getParsedOrderData/getParsedOrderData';
import {trackConversion} from '../trackConversion/trackConversion';

type ParamsType<T> = {
    getOrderId: (store: T) => string;
};

/**
 * Custom hook for impact radius analytics
 * Returns a function that prepares data for tracking conversion sale.
 * @template T - The type of the Redux store.
 * @param {Object} params - The parameters for the function.
 * @param {function(store: T): string} params.getOrderId - A function that gets the order ID from the Redux store(Mobile or Desktop).
 * @returns {function(): Promise<void>} - A function that prepares data for tracking conversion sale.
 */
export const useImpactRadius = <T>(params: ParamsType<T>): (() => Promise<void>) => {
    const {getOrderId} = params;
    const orderId = useSelector(getOrderId);
    const customer = useGetCustomerData();

    return async (): Promise<void> => {
        const {quoteItems, totals} = getParsedOrderData();
        const {insurance} = getUrlParams();
        const categoryList = getCategoryListFromLocalStorage();
        const customerStatus = customer.login_in_status;
        const {sha1} = customer;
        const clickid = localStore.get(IRE_CLICK_ID);

        return new Promise(async (resolve, reject) => {
            try {
                if (getOneTrustActiveGroups().length === 0 || insurance) return;
                if (!orderId) logWithSentry(new Error('Missing orderId for IR'));

                await preparationGenerateClickId();

                const preparedItems = quoteItems?.map((product: CartItemType) => ({
                    category:
                        categoryList.find(
                            categoryItem =>
                                categoryItem.id?.toString() === product.product_id?.toString(),
                        )?.category || null,
                    quantity: product.qty,
                    sku: product.sku,
                    subTotal: product.row_total_discounted_price,
                }));

                trackConversion(
                    {
                        clickid,
                        currencyCode: 'USD',
                        customerEmail: sha1,
                        customerId: sha1,
                        customerStatus: customerStatus === 'guest' ? 'New' : 'Returning',
                        items: preparedItems,
                        orderId,
                        orderPromoCode:
                            totals && totals.appliedCoupons?.length
                                ? totals?.appliedCoupons[0]
                                : '',
                    },
                    CONVERSION_TRACKING_ID_SALE,
                );

                trackConversion(
                    {
                        clickid,
                        customerId: sha1,
                        email: sha1,
                        orderId,
                    },
                    CONVERSION_TRACKING_ID_EMAIL_LEAD,
                );

                resolve();
            } catch (error) {
                logWithSentry(error);
                reject(error);
            }
        });
    };
};
